import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MessageSquare, Phone, ShieldCheck, ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step, setStep] = useState("mobile");
  const [mobile, setMobile] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (!/^\d{10}$/.test(mobile)) {
      toast.error("Enter a valid 10-digit mobile");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/send-otp", { mobile });
      setSessionId(r.data.session_id);
      setStep("otp");
      toast.success("OTP sent via SMS");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    if (otp.length !== 6) {
      toast.error("Enter the 6-digit OTP");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/verify-otp", { mobile, session_id: sessionId, otp });
      login(r.data.token, r.data.user);
      toast.success(`Welcome, ${r.data.user.name}`);
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OTP invalid");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-5">
      {/* Left auth pane */}
      <div className="col-span-1 flex items-center justify-start bg-background px-8 py-12 lg:col-span-2 lg:px-16">
        <div className="w-full max-w-md">
          <div className="mb-14 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center bg-primary text-primary-foreground">
              <MessageSquare className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">WABA CRM</div>
              <div className="font-display text-lg font-bold text-foreground">Staff Console</div>
            </div>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="font-display text-4xl font-black leading-none tracking-tight text-foreground sm:text-5xl"
          >
            Sign in to your <span className="text-primary">console</span>.
          </motion.h1>
          <p className="mt-4 max-w-sm text-sm text-muted-foreground">
            Access customer conversations, send approved WhatsApp templates, and manage your staff — all from one place.
          </p>

          <div className="mt-12 border border-border p-8">
            {step === "mobile" ? (
              <form onSubmit={sendOtp} className="space-y-6" data-testid="login-mobile-form">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]" htmlFor="mobile">
                    Mobile Number
                  </Label>
                  <div className="mt-2 flex items-stretch border border-input focus-within:ring-2 focus-within:ring-ring">
                    <div className="flex items-center gap-1.5 border-r border-input bg-muted px-3 text-sm text-muted-foreground">
                      <Phone className="h-3.5 w-3.5" aria-hidden="true" />
                      +91
                    </div>
                    <Input
                      id="mobile"
                      inputMode="numeric"
                      autoComplete="tel"
                      maxLength={10}
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                      placeholder="9855747777"
                      className="rounded-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                      data-testid="login-mobile-input"
                    />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    An OTP will be sent to your registered mobile via SMS.
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-sm text-sm font-semibold uppercase tracking-widest"
                  data-testid="login-send-otp-btn"
                >
                  {loading ? "Sending…" : "Send OTP"}
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                </Button>
              </form>
            ) : (
              <form onSubmit={verifyOtp} className="space-y-6" data-testid="login-otp-form">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">Enter OTP</Label>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Sent to <span className="font-mono text-foreground">+91 {mobile}</span>
                  </p>
                  <div className="mt-4">
                    <InputOTP
                      maxLength={6}
                      value={otp}
                      onChange={setOtp}
                      data-testid="login-otp-input"
                    >
                      <InputOTPGroup>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <InputOTPSlot key={i} index={i} className="h-12 w-12 rounded-none border-input text-lg" />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setStep("mobile");
                      setOtp("");
                    }}
                    className="rounded-sm"
                    data-testid="login-back-btn"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 rounded-sm text-sm font-semibold uppercase tracking-widest"
                    data-testid="login-verify-otp-btn"
                  >
                    {loading ? "Verifying…" : "Verify & Sign In"}
                    <ShieldCheck className="ml-2 h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </form>
            )}
          </div>

          <div className="mt-8 text-xs text-muted-foreground">
            <span className="font-bold uppercase tracking-[0.2em]">Secured by</span> 2Factor.in · WhatsApp Business API
          </div>
        </div>
      </div>

      {/* Right hero pane */}
      <div className="relative col-span-1 hidden overflow-hidden bg-foreground lg:col-span-3 lg:block">
        <img
          src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxjdXN0b21lciUyMHNlcnZpY2UlMjBkYXNoYm9hcmR8ZW58MHx8fHwxNzg1Mjk2MDczfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/40 to-black/70" aria-hidden="true" />
        <div className="relative z-10 flex h-full flex-col justify-between p-16 text-white">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.32em] text-white/70">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
            System live · WABA connected
          </div>
          <div>
            <div className="mb-4 text-xs font-bold uppercase tracking-[0.32em] text-white/60">The mission</div>
            <h2 className="font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl">
              One inbox. Every customer. Zero missed conversations.
            </h2>
            <p className="mt-6 max-w-lg text-base text-white/70">
              Route every WhatsApp message to the right staff member, ship approved templates in seconds, and keep a
              single source of truth for your customer conversations.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-8 border-t border-white/10 pt-8">
            <Stat label="Uptime" value="99.9%" />
            <Stat label="Templates" value="Meta-approved" />
            <Stat label="Auth" value="OTP + JWT" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">{label}</div>
      <div className="mt-1 font-display text-xl font-bold text-white">{value}</div>
    </div>
  );
}
